/// <reference types="vite/client" />
import { Buffer } from 'buffer';

import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  Firestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import {
  // connectFunctionsEmulator,
  Functions,
  getFunctions,
} from 'firebase/functions';
import {
  RemoteConfig,
  getRemoteConfig,
  ensureInitialized,
  fetchAndActivate,
} from 'firebase/remote-config';
import { getStorage } from 'firebase/storage';

import { rollbar } from './utils';

type FirebaseConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
};

const encodedConfig = import.meta.env.VITE_ENCODED_FIREBASE_CONFIG;
if (!encodedConfig) {
  throw new Error('VITE_ENCODED_FIREBASE_CONFIG is not defined.');
}

const decodedJson: string = Buffer.from(encodedConfig, 'base64').toString(
  'utf-8'
);
const firebaseConfig: FirebaseConfig = JSON.parse(decodedJson);

const app: FirebaseApp = initializeApp(firebaseConfig);

// App Check starts once the first screen has painted (Module calls startAppCheck).
// The Firebase SDKs only wait for a token after App Check is initialized and no
// backend enforces it (verified 2026-09-16: every embedded-counselor project is
// UNENFORCED), so the ~350 kB reCAPTCHA download no longer gates the partner fetch
// and anonymous sign-in. Call this at module scope again if enforcement is turned on.
let appCheckStarted = false;
export const startAppCheck = () => {
  if (appCheckStarted || process.env.NODE_ENV === 'test') return;
  appCheckStarted = true;
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(
      import.meta.env.VITE_ENTERPRISE_CAPTCHA_KEY
    ),
    isTokenAutoRefreshEnabled: true,
  });
};

let db: Firestore;

try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
} catch (err) {
  rollbar.warning(
    'Failed to enable offline support. Falling back to memory cache.',
    { errorMsg: err }
  );
  db = initializeFirestore(app, {
    localCache: memoryLocalCache(),
  });
}

const remoteConfig: RemoteConfig = getRemoteConfig();

remoteConfig.settings = {
  minimumFetchIntervalMillis: 9000000,
  fetchTimeoutMillis: 10000,
};

// Remote Config activates in the background; readers await `remoteConfigReady`
// (getFirebaseRemoteConfig / useFirebaseRemoteConfig) so booting no longer waits
// on this round trip. A failure here falls back to in-app defaults instead of
// taking the whole module graph down with it.
export const remoteConfigReady: Promise<void> = ensureInitialized(remoteConfig)
  .then(() => fetchAndActivate(remoteConfig))
  .then(() => undefined)
  .catch((error) => {
    rollbar.warning(
      'Remote config could not be activated at boot; readers use defaults',
      error as Error
    );
  });

const firebaseFunctions: Functions = getFunctions(app);

export const storage = getStorage(app, import.meta.env.VITE_LEGAL_BUCKET_URL);
// Firebase retries for 2 minutes by default; the user is blocked while a legal
// doc is missing, so fail fast enough to report it.
storage.maxOperationRetryTime = 15_000;

// Connect to the local emulator
//connectFunctionsEmulator(firebaseFunctions, 'localhost', 5001);

export { app, db, remoteConfig, firebaseFunctions };
